import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Trophy, 
  TrendingUp, 
  Activity, 
  Users, 
  Target,
  Star
} from 'lucide-react';
import { skinRankingsAPI } from '../services/api';
import toast from 'react-hot-toast';
import { getWeaponIcon } from '../utils/weaponIcons';
import { RankingChart, ScoreChart } from '../components/charts';
import DMarketPricing from '../components/DMarketPricing';

const SkinDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [skinData, setSkinData] = useState(null);
  const [usageData, setUsageData] = useState([]);
  const [usagePagination, setUsagePagination] = useState(null);
  const [rankingChartData, setRankingChartData] = useState([]);
  const [scoreChartData, setScoreChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(false);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('1m');
  const [currentPage, setCurrentPage] = useState(1);
  const [topTeams, setTopTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);

  const periods = [
    { key: '1w', label: '1 Week' },
    { key: '1m', label: '1 Month' },
    { key: '3m', label: '3 Months' },
    { key: '6m', label: '6 Months' },
    { key: '1y', label: '1 Year' },
    { key: 'all', label: 'All Time' }
  ];

  useEffect(() => {
    loadSkinData();
    loadUsageData();
    loadChartData();
    loadTopTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    loadChartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPeriod]);

  useEffect(() => {
    loadUsageData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const loadSkinData = async () => {
    try {
      const response = await skinRankingsAPI.getSkinDetails(id);
      setSkinData(response.data.data);
    } catch (error) {
      toast.error('Failed to load skin details');
      console.error('Error loading skin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsageData = async () => {
    setUsageLoading(true);
    try {
      const response = await skinRankingsAPI.getSkinUsageDetails(id, currentPage, 25);
      setUsageData(response.data.data);
      setUsagePagination(response.data.pagination);
    } catch (error) {
      toast.error('Failed to load usage data');
      console.error('Error loading usage data:', error);
    } finally {
      setUsageLoading(false);
    }
  };

  const loadChartData = async () => {
    setChartsLoading(true);
    try {
      const [rankingResponse, scoreResponse] = await Promise.all([
        skinRankingsAPI.getRankingPositionChartData(id, selectedPeriod),
        skinRankingsAPI.getScoreChangesChartData(id, selectedPeriod)
      ]);
      setRankingChartData(rankingResponse.data.data);
      setScoreChartData(scoreResponse.data.data);
    } catch (error) {
      toast.error('Failed to load chart data');
      console.error('Error loading chart data:', error);
    } finally {
      setChartsLoading(false);
    }
  };

  const loadTopTeams = async () => {
    setTeamsLoading(true);
    try {
      const response = await skinRankingsAPI.getTopTeamsUsingSkin(id, 3);
      setTopTeams(response.data.data);
    } catch (error) {
      console.error('Error loading top teams:', error);
    } finally {
      setTeamsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400"></div>
      </div>
    );
  }

  if (!skinData) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-white mb-4">Skin Not Found</h2>
        <button 
          onClick={() => navigate('/')}
          className="btn-primary"
        >
          Back to Rankings
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back to Rankings</span>
        </button>

        {/* Skin Info */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Card */}
          <div className="lg:col-span-3 card p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Skin Image */}
              <div className="lg:col-span-1">
                <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-700">
                  {skinData.image_url ? (
                    <img
                      src={skinData.image_url}
                      alt={`${skinData.weapon_name} | ${skinData.skin_name}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className={`absolute inset-0 flex items-center justify-center text-gray-400 ${skinData.image_url ? 'hidden' : 'flex'}`}
                    style={{ display: skinData.image_url ? 'none' : 'flex' }}
                  >
                    <div className="text-center">
                      <div className="text-5xl mb-4">{getWeaponIcon(skinData.weapon_name)}</div>
                      <div className="font-medium text-lg">{skinData.weapon_name}</div>
                      <div className="text-sm opacity-75">{skinData.skin_name}</div>
                    </div>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="bg-black bg-opacity-70 text-xs px-3 py-1 rounded text-white">
                      {skinData.category}
                    </span>
                  </div>
                </div>
              </div>

              {/* Skin Details */}
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <h1 className="text-3xl font-bold text-white mb-2">
                    {skinData.weapon_name} | {skinData.skin_name}
                  </h1>
                  <p className="text-gray-400 text-lg">{skinData.category}</p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-700 rounded-lg p-4 text-center">
                    <Trophy className="h-6 w-6 text-cs2-gold mx-auto mb-2" />
                    <div className="text-2xl font-bold text-white">#{skinData.rank_position}</div>
                    <div className="text-sm text-gray-400">Global Rank</div>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-4 text-center">
                    <Target className="h-6 w-6 text-primary-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-white">#{skinData.category_rank || 'N/A'}</div>
                    <div className="text-sm text-gray-400">Category Rank</div>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-4 text-center">
                    <Activity className="h-6 w-6 text-green-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-white">{skinData.usage_count}</div>
                    <div className="text-sm text-gray-400">Total Usage</div>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-4 text-center">
                    <Star className="h-6 w-6 text-purple-400 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-white">{skinData.total_score?.toFixed(2)}</div>
                    <div className="text-sm text-gray-400">Total Score</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Top Teams */}
          <div className="lg:col-span-1 card p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
              <Users className="h-5 w-5 text-primary-400" />
              <span>Top Teams</span>
            </h3>
            
            {teamsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400"></div>
              </div>
            ) : topTeams.length > 0 ? (
              <div className="space-y-3">
                {topTeams.map((team, index) => (
                  <div key={team.team_name} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                        {index + 1}
                      </div>
                      <div>
                        <div className="font-medium text-white text-sm">{team.team_name}</div>
                        {team.avg_rank && (
                          <div className="text-xs text-gray-400">Rank #{team.avg_rank}</div>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-primary-400">{team.usage_count}</div>
                      <div className="text-xs text-gray-400">uses</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No team data available</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* DMarket Pricing Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <DMarketPricing 
          weaponName={skinData.weapon_name} 
          skinName={skinData.skin_name} 
        />
      </motion.div>

      {/* Charts Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="card p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-white flex items-center space-x-2">
            <TrendingUp className="h-5 w-5 text-primary-400" />
            <span>Performance Charts</span>
          </h3>
          
          <div className="flex space-x-2">
            {periods.map((period) => (
              <button
                key={period.key}
                onClick={() => setSelectedPeriod(period.key)}
                className={`px-3 py-1 rounded text-sm transition-colors ${
                  selectedPeriod === period.key
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-700 text-gray-400 hover:text-white'
                }`}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>

        {chartsLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400 mx-auto"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Ranking Position Chart */}
            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="text-lg font-medium text-white mb-4 flex items-center space-x-2">
                <Target className="h-4 w-4 text-primary-400" />
                <span>Ranking Position Over Time</span>
                {rankingChartData.length > 0 && (
                  <span className="text-xs text-gray-400 ml-auto">
                    {rankingChartData.length} data points • {periods.find(p => p.key === selectedPeriod)?.label}
                  </span>
                )}
              </h4>
              <RankingChart 
                data={rankingChartData} 
                period={periods.find(p => p.key === selectedPeriod)?.label}
              />
            </div>

            {/* Score Changes Chart */}
            <div className="bg-gray-700 rounded-lg p-4">
              <h4 className="text-lg font-medium text-white mb-4 flex items-center space-x-2">
                <Activity className="h-4 w-4 text-green-400" />
                <span>Score Changes Over Time</span>
                {scoreChartData.length > 0 && (
                  <span className="text-xs text-gray-400 ml-auto">
                    {scoreChartData.length} data points • Gains: {scoreChartData.filter(d => d.is_gain).length} • Decays: {scoreChartData.filter(d => d.is_decay).length}
                  </span>
                )}
              </h4>
              <ScoreChart 
                data={scoreChartData} 
                period={periods.find(p => p.key === selectedPeriod)?.label}
              />
            </div>
          </div>
        )}
      </motion.div>

      {/* Usage Details Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="card p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-white flex items-center space-x-2">
            <Users className="h-5 w-5 text-primary-400" />
            <span>Match Usage Details</span>
          </h3>
          
          {usagePagination && (
            <div className="text-sm text-gray-400">
              Showing {usagePagination.total > usagePagination.max_available ? 'latest' : ''} {Math.min(usagePagination.total, usagePagination.max_available)} of {usagePagination.actual_total} total uses
              {usagePagination.actual_total > usagePagination.max_available && (
                <span className="text-yellow-400 ml-1">(max {usagePagination.max_available} available)</span>
              )}
            </div>
          )}
        </div>

        {usageLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-400 mx-auto"></div>
          </div>
        ) : usageData.length > 0 ? (
          <div className="space-y-4">
            <div className="space-y-3">
              {usageData.map((usage, index) => (
                <motion.div
                  key={usage.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-gray-700 rounded-lg p-4"
                >
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <div className="text-sm text-gray-400">Player</div>
                      <div className="text-white font-medium">{usage.player_name || 'Unknown'}</div>
                      <div className="text-xs text-gray-500">{usage.team || 'Unknown Team'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Match</div>
                      <div className="text-white font-medium">
                        {usage.team_a_name || usage.team_a || 'Team A'} vs {usage.team_b_name || usage.team_b || 'Team B'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {usage.tournament_name || 'Unknown Tournament'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Condition</div>
                      <div className="text-white">{usage.wear_name || 'Unknown'}</div>
                      {usage.wear_float && (
                        <div className="text-xs text-gray-500">Float: {parseFloat(usage.wear_float).toFixed(4)}</div>
                      )}
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">Date</div>
                      <div className="text-white">{formatDate(usage.usage_date)}</div>
                      {usage.significance && (
                        <div className="text-xs text-gray-500">Rating: {usage.significance}/10</div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {usagePagination && usagePagination.total_pages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t border-gray-700">
                <div className="text-sm text-gray-400">
                  Page {usagePagination.current_page} of {usagePagination.total_pages}
                  <span className="ml-2">({usagePagination.per_page} per page)</span>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={!usagePagination.has_prev}
                    className="px-3 py-1 text-sm bg-gray-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
                  >
                    Previous
                  </button>
                  
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, usagePagination.total_pages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(
                      usagePagination.total_pages - 4,
                      Math.max(1, currentPage - 2)
                    )) + i;
                    
                    if (pageNum > usagePagination.total_pages) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 text-sm rounded transition-colors ${
                          pageNum === currentPage
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!usagePagination.has_next}
                    className="px-3 py-1 text-sm bg-gray-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No usage data available</p>
          </div>
        )}

      </motion.div>
    </div>
  );
};

export default SkinDetailPage;
